import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, User } from "../lib/api";

interface AuthCtx {
  user: User | null | false;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, ref?: string, marketing?: boolean) => Promise<void>;
  googleSession: (sessionId: string, ref?: string) => Promise<boolean>;
  acceptInvite: (token: string, password: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>(null as any);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | false>(null);
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google: the callback page exchanges the session first, so skip the /me probe.
    if (window.location.hash?.includes("session_id=")) return;
    refresh();
  }, [refresh]);
  useEffect(() => {
    const onExpired = () => { qc.clear(); setUser(false); toast.message("Your session has ended — please sign in again."); };
    window.addEventListener("cellar:session-expired", onExpired);
    return () => window.removeEventListener("cellar:session-expired", onExpired);
  }, [qc]);

  const apply = useCallback((data: any) => {
    qc.clear();
    localStorage.setItem("cellar_token", data.access_token);
    setUser(data.user);
  }, [qc]);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post("/auth/login", { email, password });
    apply(data);
  }, [apply]);
  const register = useCallback(async (email: string, password: string, name: string, ref = "", marketing = false) => {
    const { data } = await api.post("/auth/register", { email, password, name, ref, marketing });
    apply(data);
  }, [apply]);
  const googleSession = useCallback(async (sessionId: string, ref = "") => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId, ref });
    apply(data);
    return !!data.created;
  }, [apply]);
  const acceptInvite = useCallback(async (token: string, password: string) => {
    const { data } = await api.post("/auth/invite/accept", { token, password });
    apply(data);
  }, [apply]);
  const resetPassword = useCallback(async (token: string, password: string) => {
    const { data } = await api.post("/auth/reset-password", { token, password });
    apply(data);
  }, [apply]);
  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const { data } = await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
    localStorage.setItem("cellar_token", data.access_token);
    setUser(data.user);
  }, []);
  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => undefined);
    localStorage.removeItem("cellar_token");
    qc.clear();
    setUser(false);
  }, [qc]);

  const value = useMemo(() => ({ user, login, register, googleSession, acceptInvite, resetPassword, changePassword, logout, refresh }), [user, login, register, googleSession, acceptInvite, resetPassword, changePassword, logout, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
